/**
 * SX.bet Sports Navigation Hook
 *
 * This hook fetches comprehensive sports navigation data including:
 * - All sports available on SX.bet
 * - All active leagues for each sport
 * - Formatted data for Navigation/Sidebar components
 *
 * Returns:
 * - navigation: Formatted data for Navigation component
 * - leagues: Raw active leagues data
 * - sports: Raw sports data
 *
 * For fixture-specific operations, use the dedicated useSxFixtures hooks.
 */

import { useQuery } from "@tanstack/react-query"
import constants from "@/helpers/constants"
import { transformSxNavigationData } from "../utils"
import type { SxLeagueActiveResponse } from "../types/markets"
import useChainProviderHook from "@/hooks/useChainProvider"
import { sports } from "../constants/sports"

/**
 * SX.bet Sports Navigation Hook
 *
 * This hook fetches comprehensive sports navigation data including:
 * - All sports available on SX.bet
 * - All active leagues for each sport
 * - Formatted data for Navigation/Sidebar components
 *
 * Returns:
 * - navigation: Formatted data for Navigation component (undefined while loading)
 * - leagues: Raw active leagues data (undefined while loading)
 * - sports: Raw sports data (undefined while loading)
 * - isLoading: Boolean indicating if data is being fetched
 * - isError: Boolean indicating if there was an error
 * - error: Error object if any
 * - refetch: Function to manually refetch data
 *
 * For fixture-specific operations, use the dedicated useSxFixtures hooks.
 */

export const useSxSportsNavigation = () => {
  const { isSXChain, chainId } = useChainProviderHook()

  const query = useQuery({
    queryKey: ["sx-sports-navigation", isSXChain, chainId],
    queryFn: async () => {
      const getSxBetApiUrl = constants.getSxBetApiUrl()

      const leaguesResponse = await fetch(
        `${getSxBetApiUrl}/leagues/active?chainId=${chainId}`
      )

      const leaguesData: SxLeagueActiveResponse = await leaguesResponse.json()

      return {
        navigation: transformSxNavigationData(sports, leaguesData.data),
        leagues: leaguesData.data,
        sports: sports,
      }
    },
    enabled: isSXChain,
    gcTime: 1000 * 30, // 30 seconds
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}
